import React, { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createChart, LineSeries, LineStyle } from 'lightweight-charts';
import type { IChartApi, ISeriesApi, Time, UTCTimestamp } from 'lightweight-charts';
import { fetchVixStrength, closeTimeOf } from '../services/vixStrengthService';
import { TZ } from '../utils/marketTime';
import { isMarketOpen } from '../utils/marketCalendar';

/**
 * Anti-VIX — the "ANTI-VIX (JPokoTrades)" Pine v6 indicator, on Market Overview.
 *
 * ⚠️ **Reads BACKWARDS from a volatility chart, deliberately.** The series is
 * inverted, so a HIGH VIX prints a LOW value. The lower band is therefore the
 * BUY zone and the upper band the SELL zone. The server enforces this; the
 * bands arrive in the payload.
 *
 * **LIVE** — QuantForge `GET /v1/steamroom/vix-strength`, via
 * `services/vixStrengthService`. The server computes the indicator and returns
 * readings; this file only plots them.
 *
 * ⚠️ **The bands come from the payload, not from constants here.** They are
 * served precisely so the guide lines cannot drift from the thresholds the
 * server actually applied. Hardcoding 30/60 would reintroduce that drift.
 *
 * ⚠️ **`partial` is a success state.** VIX history began 2026-07-13 under a
 * 90-day TTL, so the series is short of a full window until roughly
 * mid-September 2026 — its values are correct, there are simply fewer of them.
 * Rendering that as an error blanks a working chart for weeks.
 *
 * Renders its own card. The classes match the page-local `Panel` and
 * `MarketBreadth` rather than importing either — `Panel` lives inside
 * `MarketOverviewPage`, which imports this file, so sharing it would be a
 * circular import. Components that are cards owning their own chrome is the
 * existing convention here, not a workaround.
 */

/**
 * Brand palette, by value. lightweight-charts writes these into a canvas, where
 * a Tailwind class name means nothing — so these are the hexes from
 * `index.css`, not `var(--color-…)`.
 *
 * ⚠️ **The band colours are NOT the TradingView ones, and the swap is
 * intentional.** The script draws green below and red above; here the buy band
 * is Up/Call blue and the sell band Down/Put red, which is how every other
 * panel in this app signs direction. Green would read as a different system.
 */
const COLORS = {
  background: '#161616', // Chart Well — identical to Panel, so the pane sits flush
  grid: 'rgba(255,255,255,0.05)', // Same hairline the earnings charts use
  border: '#2b2b2b', // Hairline borders
  text: '#c7c2b8', // Text Muted
  line: '#c2a14e', // Accent Gold — the subject of the pane
  buy: '#1c76a2', // Up / Call blue — the LOWER band
  sell: '#d8483b', // Down / Put red — the UPPER band
};

/**
 * ⚠️ Explicit pixel height, not `h-full`. Every ancestor here is auto-height,
 * so a percentage resolves against zero and `autoSize` measures an empty box
 * and draws nothing. Same trap documented on `OverviewChart`'s CHART_HEIGHT.
 */
const CHART_HEIGHT = 150;

/**
 * Empty bar slots held open at the right of the plot.
 *
 * ⚠️ **This is horizontal margin, and `scaleMargins` cannot provide it.** The
 * BUY and SELL titles are drawn inside the plot, anchored to its right edge, at
 * their own price. With the series running to the last pixel the newest bars
 * pass straight through those boxes — and the curve is nearest a band exactly
 * when it is about to cross one, so the label is obscured at the only moment it
 * matters.
 *
 * Sized to clear the widest title box rather than picked by eye: four
 * characters of bold 10px plus the box padding is a little over 40px, and at
 * this pane's width a bar is roughly 4–5px.
 */
const RIGHT_PAD_BARS = 12;

/**
 * Poll cadence, matching Market Overview's own five-minute clip.
 *
 * ⚠️ Declared here rather than imported from `MarketOverviewPage`. That module
 * imports this one, so reaching back for its constant is a circular import —
 * the same reason this component owns its card chrome instead of sharing the
 * page's `Panel`.
 */
const PAGE_REFRESH_MS = 5 * 60 * 1000;

export function AntiVix(): React.ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Line'> | null>(null);

  /**
   * ⚠️ **Refetched on the page's clock, not the bar's.** A 65-minute bar cannot
   * change more often than every 65 minutes, but its FINAL value settles only
   * when the bar closes — so polling on the page cadence picks the settled
   * value up promptly rather than showing a stale bar for up to an hour.
   * The server caches for 300s, so the extra calls are cheap.
   *
   * ⚠️ Polling STOPS when the market is closed, matching every other panel on
   * this page. VIX does not print overnight, so an open-ended interval would
   * re-request an unchanging series until the tab is shut.
   */
  const { data: strength, isLoading, isError } = useQuery({
    queryKey: ['antiVix'],
    queryFn: () => fetchVixStrength(),
    staleTime: PAGE_REFRESH_MS,
    refetchInterval: () => (isMarketOpen() ? PAGE_REFRESH_MS : false),
  });

  const points = useMemo(() => strength?.points ?? [], [strength]);

  const latest = points.length ? points[points.length - 1].value : null;

  /**
   * This card's OWN freshness stamp, as the newest bar's CLOSE.
   *
   * ⚠️ **The server stamps points at the bar's OPEN**, matching `/v1/bars`. Read
   * directly, that would report the panel one whole bar — 65 minutes — fresher
   * than it is, so every label goes through `closeTimeOf`.
   *
   * ⚠️ **Not the page-level `PageFreshness`, deliberately.** That tracks a
   * five-minute refresh; this series moves on a 65-minute bar, so borrowing the
   * page stamp would claim the indicator had updated twelve times more often
   * than it can.
   */
  const lastBarMs = useMemo(() => {
    if (!points.length || !strength) return null;
    return closeTimeOf(points[points.length - 1], strength.intervalMinutes) * 1000;
  }, [points, strength]);

  useEffect(() => {
    if (!containerRef.current || !strength || points.length === 0) return;

    const data: Array<{ time: Time; value: number }> = points.map((p) => ({
      time: p.time as UTCTimestamp,
      value: p.value,
    }));

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { color: COLORS.background },
        textColor: COLORS.text,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: COLORS.grid },
        horzLines: { color: COLORS.grid },
      },
      crosshair: { mode: 1 },
      // No `scaleMargins` — the library's own defaults (0.2 top, 0.1 bottom).
      // Tuned values were tried at 0.1/0.08, 0.24/0.22 and 0.18/0.12; none read
      // better than leaving it alone, so this is deliberately not configured.
      rightPriceScale: { borderColor: COLORS.border },
      // 65-minute bars, so the date is what orients you, not the clock time.
      timeScale: { borderColor: COLORS.border, timeVisible: false, secondsVisible: false },
      /**
       * ⚠️ Pan/zoom off, matching `TickerDetail/OverviewChart`. Market Overview
       * is a scrolling page, and a live chart swallows the wheel and zooms
       * instead of letting the page scroll past it — the pane becomes a trap
       * the cursor has to be steered around.
       */
      handleScroll: false,
      handleScale: false,
    });

    const series = chart.addSeries(LineSeries, {
      color: COLORS.line,
      lineWidth: 2,
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
      /**
       * Autoscaled — no `autoscaleInfoProvider`, so the price scale fits the
       * visible data the way TradingView does.
       *
       * ⚠️ **Price lines do NOT participate in autoscale.** The library builds
       * the range from series data and primitives only; `createPriceLine`
       * levels are drawn afterwards and never widen it. So in a window where
       * the reading never approaches 30 or 60, that band scrolls off the pane
       * and the level silently stops being visible.
       *
       * Accepted because the reading crosses both bands routinely. If a band
       * ever goes missing on real data, the fix is an `autoscaleInfoProvider`
       * that merges the default range with the two levels rather than a return
       * to fixed bounds.
       */
    });

    series.setData(data);

    // Buy LOW, sell HIGH — inverted, see the file header. Levels come from the
    // payload so they cannot drift from the server's own thresholds.
    const bands = strength.bands;
    series.createPriceLine({
      price: bands.lower,
      color: COLORS.buy,
      lineWidth: 1,
      lineStyle: LineStyle.Solid,
      axisLabelVisible: true,
      title: 'BUY',
    });
    series.createPriceLine({
      price: bands.upper,
      color: COLORS.sell,
      lineWidth: 1,
      lineStyle: LineStyle.Solid,
      axisLabelVisible: true,
      title: 'SELL',
    });

    /**
     * ⚠️ Not `fitContent()`. That fits the bars to the last pixel of the plot,
     * leaving no room on the right — and `timeScale.rightOffset` does not
     * survive it either, because fitting overwrites the visible range it sets.
     *
     * Asking for a range that runs PAST the final bar is what reserves the
     * gap: the extra logical slots hold no data, so they render as empty
     * space the BUY and SELL titles can sit in.
     */
    chart.timeScale().setVisibleLogicalRange({
      from: 0,
      to: data.length - 1 + RIGHT_PAD_BARS,
    });

    chartRef.current = chart;
    seriesRef.current = series;

    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [points, strength]);

  return (
    <section className="p-4 rounded-lg border border-dark-border bg-dark-surface">
      <div className="flex items-baseline gap-3 pb-2 mb-3 flex-wrap border-b border-dark-border">
        <h2 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Anti-VIX</h2>

        {latest != null && (
          <span
            className={`font-numeric text-xs font-semibold ${
              strength && latest <= strength.bands.lower
                ? 'text-bull-blue'
                : strength && latest >= strength.bands.upper
                  ? 'text-bear-red'
                  : 'text-gray-200'
            }`}
          >
            {latest.toFixed(2)}
          </span>
        )}

        {/* Beside the value it describes: this stamp says when THAT number was
            printed, and the two are meaningless apart. */}
        {lastBarMs != null && (
          <span
            className="font-numeric text-[11px] text-gray-400 whitespace-nowrap"
            title={`Anti-VIX prints one bar every ${strength?.intervalMinutes ?? 65} minutes, so this stamp moves on its own clock — not with the rest of the page.`}
          >
            <span className="text-gray-500">Updated </span>
            {/* Date and time formatted separately so they join with a space
                rather than the comma a single call inserts. The date matches
                `fmtUsDate` (MM/DD/YYYY in ET), which is what `PageFreshness`
                and the rest of the app print. Time stays en-GB: en-US with
                `hour12: false` renders midnight as 24:00 in some engines. */}
            {new Date(lastBarMs).toLocaleDateString('en-US', {
              timeZone: TZ,
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            })}
            {' '}
            {new Date(lastBarMs).toLocaleTimeString('en-GB', {
              timeZone: TZ,
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })}
            {' ET'}
          </span>
        )}

        {/* ⚠️ Says the plot is SHORT, not that it is wrong. `partial` values are
            exactly as correct as a full window's — VIX history began
            2026-07-13 under a 90-day TTL, so there are simply fewer of them.
            Reads as information, not as a warning about the numbers. */}
        {strength?.status === 'partial' && (
          <span
            className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider leading-none bg-accent-yellow/20 text-accent-yellow border border-accent-yellow/40"
            title={`Building history: ${strength.barsAvailable} of ${strength.barsRequired} bars. Values shown are correct; the plot is shorter than its full window.`}
          >
            {strength.barsAvailable}/{strength.barsRequired} bars
          </span>
        )}

        {/* Brand mark. The badge lockup already contains the wordmark, so no
            adjacent text — same asset and reasoning as `Navigation`. */}
        <img
          src="/logo-badge.svg"
          alt="Fuzion Trading"
          className="ml-auto h-5 w-auto object-contain flex-none"
        />
      </div>

      {/* ⚠️ Three distinct empty states, deliberately. A feed that is down, a
          window that has not filled and a chart still loading look identical if
          collapsed into one message — and only one of them is a fault. The
          container stays mounted in every case so the effect keeps its ref. */}
      {isError && (
        <div
          className="flex items-center justify-center text-xs text-gray-500"
          style={{ height: CHART_HEIGHT }}
        >
          Unavailable
        </div>
      )}

      {!isError && isLoading && (
        <div
          className="flex items-center justify-center text-xs text-gray-500"
          style={{ height: CHART_HEIGHT }}
        >
          Loading…
        </div>
      )}

      {!isError && !isLoading && strength?.status === 'warming_up' && (
        <div
          className="flex items-center justify-center text-center text-xs text-gray-500 px-4"
          style={{ height: CHART_HEIGHT }}
        >
          Building history — {strength.barsAvailable} of {strength.barsRequired} bars.
        </div>
      )}

      <div
        ref={containerRef}
        style={{ height: CHART_HEIGHT }}
        className={
          isError || isLoading || strength?.status === 'warming_up' ? 'hidden' : undefined
        }
      />
    </section>
  );
}
